import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Save, Upload, Trash2, ArrowLeft, Plus, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminManualData = () => {
  const [authed, setAuthed] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [advertisers, setAdvertisers] = useState([]);
  const [advertiserId, setAdvertiserId] = useState('');
  const [sourceLabel, setSourceLabel] = useState('theCut');
  const newRow = (date = new Date().toISOString().slice(0, 10)) => ({
    _id: (crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`),
    date, other: '', revenue: '',
  });
  const [rows, setRows] = useState([newRow()]);
  const [csvText, setCsvText] = useState('');
  const [banner, setBanner] = useState(null);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState([]);

  const headers = { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' };

  const fetchAdvertisers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/advertisers`, { headers });
      if (res.ok) {
        const data = await res.json();
        setAdvertisers(data.advertisers || []);
      }
    } catch (err) {
      console.error('Failed to fetch advertisers:', err);
    }
  };

  const fetchExisting = async (advId) => {
    if (!advId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/manual/export/${advId}?days=60`, { headers });
      if (res.ok) {
        const data = await res.json();
        setExisting(data.rows || []);
      }
    } catch (err) {
      console.error('Failed to fetch manual rows:', err);
    }
  };

  useEffect(() => {
    if (authed) fetchAdvertisers();
  }, [authed]);

  useEffect(() => {
    if (advertiserId) fetchExisting(advertiserId);
  }, [advertiserId]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/auth`, {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' },
      });
      if (res.ok) setAuthed(true);
      else setBanner({ kind: 'error', text: 'Incorrect admin key' });
    } catch (err) {
      console.error('Admin auth failed:', err);
      setBanner({ kind: 'error', text: 'Unable to reach server. Try again.' });
    }
  };

  const addRow = () => {
    setRows([...rows, newRow('')]);
  };

  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const updateRow = (i, field, value) => {
    const next = [...rows];
    next[i] = { ...next[i], [field]: value };
    setRows(next);
  };

  const parseCsv = () => {
    if (!csvText.trim()) {
      setBanner({ kind: 'error', text: 'Paste CSV data first' });
      return;
    }
    try {
      const lines = csvText.trim().split('\n');
      const header = lines[0].toLowerCase().split(',').map(s => s.trim());
      const dateIdx = header.indexOf('date');
      let otherIdx = header.indexOf('other');
      if (otherIdx === -1) otherIdx = header.indexOf('new_clients');
      if (otherIdx === -1) otherIdx = header.indexOf('bookings');
      const revenueIdx = header.indexOf('revenue');
      if (dateIdx === -1) {
        setBanner({ kind: 'error', text: 'CSV must include a "date" column (YYYY-MM-DD)' });
        return;
      }
      const parsed = lines.slice(1).filter(l => l.trim()).map(l => {
        const c = l.split(',').map(s => s.trim());
        return {
          _id: (crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`),
          date: c[dateIdx],
          other: otherIdx > -1 ? c[otherIdx] : '',
          revenue: revenueIdx > -1 ? c[revenueIdx] : '',
        };
      });
      setRows(parsed);
      setCsvText('');
      setBanner({ kind: 'success', text: `Parsed ${parsed.length} rows. Review below, then click Save.` });
    } catch (e) {
      setBanner({ kind: 'error', text: 'CSV parse error: ' + e.message });
    }
  };

  const handleSave = async () => {
    if (!advertiserId) {
      setBanner({ kind: 'error', text: 'Pick an advertiser first' });
      return;
    }
    const cleanRows = rows
      .filter(r => r.date && /^\d{4}-\d{2}-\d{2}$/.test(r.date))
      .map(r => ({
        date: r.date,
        other: parseFloat(r.other || 0) || 0,
        revenue: parseFloat(r.revenue || 0) || 0,
      }));
    if (!cleanRows.length) {
      setBanner({ kind: 'error', text: 'No valid rows. Make sure date is YYYY-MM-DD.' });
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/manual/import`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          advertiser_id: advertiserId,
          source_label: sourceLabel,
          rows: cleanRows,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setBanner({ kind: 'success', text: `Saved ${data.upserted} rows. Advertiser's dashboard updates immediately.` });
        setRows([newRow()]);
        fetchExisting(advertiserId);
      } else {
        const err = await res.json().catch(() => ({}));
        setBanner({ kind: 'error', text: err.detail || 'Save failed' });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (date) => {
    if (!window.confirm(`Delete manual data for ${date}?`)) return;
    await fetch(`${BACKEND_URL}/api/integrations/manual/${advertiserId}/${date}`, {
      method: 'DELETE', headers,
    });
    fetchExisting(advertiserId);
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream-light px-4">
        <form onSubmit={handleLogin} className="bg-white p-8 rounded-2xl shadow-lg max-w-sm w-full">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="text-navy" />
            <h1 className="text-2xl font-bold text-navy">Admin · Manual Data</h1>
          </div>
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Admin key"
            className="w-full px-4 py-3 border border-navy/20 rounded-xl mb-3"
            data-testid="admin-manual-key"
          />
          <button type="submit" className="w-full bg-navy text-cream py-3 rounded-xl font-semibold" data-testid="admin-manual-login">
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-light py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <Link to="/admin/geofences" className="text-navy/60 hover:text-navy text-sm flex items-center gap-1 mb-1">
              <ArrowLeft size={14} /> Back to admin
            </Link>
            <h1 className="text-3xl font-bold text-navy flex items-center gap-2">
              <Upload size={28} /> Manual Conversion Data
            </h1>
            <p className="text-navy/60 text-sm mt-1">
              Paste daily numbers from any booking app (theCut, Booksy, Vagaro, etc.) on behalf of an advertiser.
            </p>
          </div>
        </div>

        {banner && (
          <div className={`mb-4 p-3 rounded-xl ${banner.kind === 'error' ? 'bg-red-50 text-red-700' :
            banner.kind === 'success' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
            <div className="flex justify-between items-center">
              <p className="text-sm font-medium">{banner.text}</p>
              <button onClick={() => setBanner(null)} className="text-xs underline">Dismiss</button>
            </div>
          </div>
        )}

        {/* Picker */}
        <div className="bg-white rounded-2xl p-5 mb-5 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Advertiser</label>
            <select value={advertiserId} onChange={(e) => setAdvertiserId(e.target.value)}
                    className="w-full px-3 py-2 border border-navy/20 rounded-lg bg-white"
                    data-testid="manual-adv-select">
              <option value="">Pick an advertiser…</option>
              {advertisers.map(a => (
                <option key={a.id} value={a.id}>{a.company_name || a.email} ({a.email})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Source label (visible to advertiser)</label>
            <input value={sourceLabel} onChange={(e) => setSourceLabel(e.target.value)}
                   placeholder="theCut / Booksy / Vagaro / Manual"
                   className="w-full px-3 py-2 border border-navy/20 rounded-lg" />
          </div>
        </div>

        {/* CSV paste */}
        <div className="bg-white rounded-2xl p-5 mb-5 shadow-sm">
          <h3 className="font-semibold text-navy mb-2">Paste from spreadsheet (optional)</h3>
          <p className="text-xs text-navy/60 mb-2">Header line: <code>date,other,revenue</code> — `other` is the optional custom metric (also accepts legacy `new_clients`/`bookings` headers).</p>
          <textarea value={csvText} onChange={(e) => setCsvText(e.target.value)}
                    rows={4} placeholder={`date,other,revenue\n2026-05-14,18,540\n2026-05-13,22,660`}
                    className="w-full px-3 py-2 border border-navy/20 rounded-lg font-mono text-sm"
                    data-testid="manual-csv-textarea" />
          <button onClick={parseCsv} className="mt-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-semibold">
            Parse CSV → fill table
          </button>
        </div>

        {/* Row editor */}
        <div className="bg-white rounded-2xl p-5 mb-5 shadow-sm" data-testid="manual-rows-editor">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-navy">Daily numbers</h3>
            <button onClick={addRow} className="text-navy text-sm flex items-center gap-1 hover:underline">
              <Plus size={14} /> Add row
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-navy/10 text-navy/60 text-left">
                  <th className="py-2 pr-2">Date</th>
                  <th className="py-2 px-2">Other (optional)</th>
                  <th className="py-2 px-2">Revenue ($)</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r._id} className="border-b border-navy/5">
                    <td className="py-2 pr-2">
                      <input type="date" value={r.date} onChange={(e) => updateRow(i, 'date', e.target.value)}
                             className="px-2 py-1 border border-navy/20 rounded w-36" />
                    </td>
                    <td className="py-2 px-2">
                      <input type="number" value={r.other} onChange={(e) => updateRow(i, 'other', e.target.value)}
                             className="px-2 py-1 border border-navy/20 rounded w-24" />
                    </td>
                    <td className="py-2 px-2">
                      <input type="number" step="0.01" value={r.revenue} onChange={(e) => updateRow(i, 'revenue', e.target.value)}
                             className="px-2 py-1 border border-navy/20 rounded w-24" />
                    </td>
                    <td className="py-2">
                      {rows.length > 1 && (
                        <button onClick={() => removeRow(i)} className="text-red-500 hover:bg-red-50 p-1 rounded">
                          <X size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={handleSave} disabled={busy || !advertiserId}
                  className="mt-4 px-6 py-3 bg-navy text-cream rounded-xl font-semibold hover:bg-navy/90 disabled:opacity-50 flex items-center gap-2"
                  data-testid="manual-save-button">
            <Save size={16} /> Save to dashboard
          </button>
        </div>

        {/* Existing rows */}
        {existing.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h3 className="font-semibold text-navy mb-3">
              Already submitted ({existing.length} day{existing.length === 1 ? '' : 's'})
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-navy/60 text-left border-b border-navy/10">
                  <tr>
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4 text-right">Other</th>
                    <th className="py-2 pr-6 text-right">Revenue</th>
                    <th className="py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {existing.map((row) => (
                    <tr key={row.date} className="border-b border-navy/5">
                      <td className="py-2 pr-4 text-navy whitespace-nowrap">{row.date}</td>
                      <td className="py-2 pr-4 text-right">{(row.manual_other ?? row.manual_new_clients) || '—'}</td>
                      <td className="py-2 pr-6 text-right font-semibold whitespace-nowrap">${(row.manual_revenue || 0).toFixed(2)}</td>
                      <td className="py-2 text-right">
                        <button onClick={() => handleDelete(row.date)} className="text-red-500 p-1 hover:bg-red-50 rounded">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminManualData;
